'use strict';
/* ==========================================================================
   core-local/look.js — "The Ground Is Moving": the lesson's collage ingredients (DESIGN.md §2), shared by the title,
   the map and (optionally) the scenes. Draws through the engine globals (g, PAL, paperRect, ink, …); every function is
   a pure function of its arguments (no clock), so it's safe in player scenes too.

   GroundLook.COL                              the lesson's rock and earth colours (DESIGN.md §2 tokens)
   GroundLook.survey(x, y, w, h, opts)         an old field-survey sheet: pale green-grey paper + faint contour lines
   GroundLook.contours(x, y, w, h, seed, a)    just the contour lines (clipped to the box)
   GroundLook.strata(y, h, opts)               a torn-paper strata band across the stage (limestone, sandstone, shale,
                                               granite, basalt, mantle, a thread of magma), top edge at y
   GroundLook.quakeMap(x, y, w, h, opts)       the REAL map: Natural Earth coastlines + every M ≥ 5 quake of 2025
                                               (Game.content.coast / .quakes, assets/data/, provenance in SOURCES.md),
                                               equirectangular, on a survey-paper card. {plates: true} adds PB2002.
   ========================================================================== */
(function () {
  const COL = {
    crust: '#b98b5e', mantle: '#d9763a', outerCore: '#f2b33d', innerCore: '#fbe7a1', ocean: '#4f7fa3', magma: '#e0533d',
    basalt: '#4a4a52', granite: '#c9b8a8', limestone: '#e8e0c8', sandstone: '#d8b98a', shale: '#8f8a7e',
    survey: '#e3e6d3', contour: 'rgba(96,120,92,0.30)', grid: 'rgba(96,120,92,0.16)',
  };

  /* faint contour rings around a few hill centres (deterministic from seed), clipped to the box */
  function contours(x, y, w, h, seed = 1, alpha = 1) {
    const r = rng(seed * 7919 + 13);
    g.save(); g.beginPath(); g.rect(x - w / 2, y - h / 2, w, h); g.clip();
    g.globalAlpha *= alpha; g.strokeStyle = COL.contour; g.lineWidth = 2; g.lineJoin = 'round';
    const hills = 3 + Math.floor(r() * 3);
    for (let k = 0; k < hills; k++) {
      const cx = x - w / 2 + r() * w, cy = y - h / 2 + r() * h, base = 30 + r() * 40, rings = 4 + Math.floor(r() * 4);
      const ph = [r() * TAU, r() * TAU, r() * TAU], sq = 0.55 + r() * 0.4;
      for (let i = 1; i <= rings; i++) {
        const rr = base * i;
        g.beginPath();
        for (let a = 0; a <= 64; a++) {
          const th = a / 64 * TAU;
          const wob = 1 + 0.16 * Math.sin(th * 2 + ph[0] + i * 0.3) + 0.09 * Math.sin(th * 3 + ph[1]) + 0.05 * Math.sin(th * 5 + ph[2] + i);
          const px = cx + Math.cos(th) * rr * wob, py = cy + Math.sin(th) * rr * wob * sq;
          if (a) g.lineTo(px, py); else g.moveTo(px, py);
        }
        g.stroke();
      }
    }
    g.restore();
  }

  /* an old survey sheet: torn pale paper, a faint grid, contour lines */
  function survey(x, y, w, h, { seed = 1, rot = 0, depth = 1.2, torn = true, grid = true, contour = 1 } = {}) {
    paperRect(x, y, w, h, COL.survey, { seed, rot, depth, torn, boil: false });
    g.save(); g.translate(x, y); g.rotate(rot);
    if (grid) {
      g.strokeStyle = COL.grid; g.lineWidth = 1.5; g.beginPath();
      for (let gx = -w / 2 + 60; gx < w / 2 - 20; gx += 120) { g.moveTo(gx, -h / 2 + 18); g.lineTo(gx, h / 2 - 18); }
      for (let gy = -h / 2 + 60; gy < h / 2 - 20; gy += 120) { g.moveTo(-w / 2 + 18, gy); g.lineTo(w / 2 - 18, gy); }
      g.stroke();
    }
    if (contour > 0) contours(0, 0, w - 36, h - 36, seed, contour);
    g.restore();
  }

  /* torn strata across the stage: each layer a torn strip (sprites: static, cached by the engine) */
  const LAYERS = [['limestone', 0.16], ['sandstone', 0.15], ['shale', 0.12], ['granite', 0.16], ['basalt', 0.12], ['magma', 0.04], ['mantle', 0.25]];
  function strata(y, h, { seed = 1, x = 960, w = 2080, tilt = -0.012 } = {}) {
    let top = y;
    const tot = LAYERS.reduce((a, l) => a + l[1], 0);
    LAYERS.forEach(([name, f], i) => {
      const lh = h * f / tot;
      // each strip overlaps the next a little so the torn edges show over the layer below
      paperRect(x, top + lh / 2 + 6, w, lh + 18, COL[name], { seed: seed * 31 + i, torn: true, depth: i ? 0.5 : 1.2, rot: tilt * (i % 2 ? -0.6 : 1), boil: false });
      top += lh;
    });
  }

  /* the real quake map (equirectangular, lon −180..180 → x, lat 84..−60 → y) */
  let coastPath = null, platePath = null, dots = null;
  function paths() {
    const C = Game.content && Game.content.coast, Q = Game.content && Game.content.quakes, B = Game.content && Game.content.plates;
    if (!coastPath && C && C.lines && typeof Path2D !== 'undefined') {
      coastPath = new Path2D();
      for (const l of C.lines) for (let i = 0; i < l.length; i += 2) (i ? coastPath.lineTo : coastPath.moveTo).call(coastPath, l[i], -l[i + 1]);
    }
    if (!platePath && B && B.lines && typeof Path2D !== 'undefined') {
      platePath = new Path2D();
      for (const l of B.lines) for (let i = 0; i < l.p.length; i += 2) {
        const jump = i && Math.abs(l.p[i] - l.p[i - 2]) > 180;         // don't draw across the date line
        (i && !jump ? platePath.lineTo : platePath.moveTo).call(platePath, l.p[i], -l.p[i + 1]);
      }
    }
    if (!dots && Q && Q.q) dots = Q.q;
  }
  function quakeMap(x, y, w, h, { seed = 5, rot = 0, depth = 1.2, plates = false, until = 366, dot = 1.6, frame = true } = {}) {
    paths();
    if (frame) survey(x, y, w, h, { seed, rot, depth, grid: false, contour: 0.35 });
    if (!coastPath) return;
    const LAT0 = 84, LAT1 = -60, sx = (w - 40) / 360, sy = (h - 40) / (LAT0 - LAT1), s = Math.min(sx, sy);
    g.save(); g.translate(x, y); g.rotate(rot);
    g.beginPath(); g.rect(-w / 2 + 16, -h / 2 + 16, w - 32, h - 32); g.clip();
    g.translate(0, (LAT0 + LAT1) / 2 * s); g.scale(s, s);                // centre lat (84 + −60)/2 at y = 0
    g.lineWidth = 1.4 / s; g.strokeStyle = 'rgba(43,36,32,0.55)'; g.lineJoin = 'round'; g.stroke(coastPath);
    if (plates && platePath) { g.lineWidth = 1.6 / s; g.strokeStyle = 'rgba(224,83,61,0.55)'; g.stroke(platePath); }
    if (dots) {
      g.fillStyle = 'rgba(224,83,61,0.75)'; g.beginPath();
      const r = dot / s;
      for (const q of dots) { if (q[4] > until) break; g.rect(q[0] - r, -q[1] - r, 2 * r, 2 * r); }
      g.fill();
    }
    g.restore();
  }

  window.GroundLook = { COL, survey, contours, strata, quakeMap, LAYERS };
})();
